/**
 * 精灵库 + 程序化怪物拼装
 * 输入：LLM 返回的 MonsterDesc（含 element / bodyType）
 * 输出：64x64 canvas 像素图 + 待机动画帧
 *
 * 五行系统：25 体型 = 金木水火土 × 5
 * - 金：锋刃兵 / 齿轮傀 / 钢骨人 / 镜灵 / 铜钟
 * - 木：树人长老 / 藤蔓女妖 / 菌伞灵 / 竹节妖 / 木鸢
 * - 水：水母灯 / 冰棱鬼 / 潮汐灵 / 雨滴精 / 深潜者
 * - 火：烛灵 / 炎魔 / 灰烬使 / 岩浆兽 / 硫磺鬼
 * - 土：山岭巨灵 / 泥塑童 / 沙偶 / 矿石兽 / 陶俑
 */

import type { MonsterDesc, Palette, Element } from "../shared/types";
import { ALL_BODY_TYPES, ELEMENTS } from "./elements";

/**
 * 25 个元素专属调色板（每元素 5 个：主色 / 辅色 / 高光 / 暗色 / 强调）
 */
export const ELEMENT_PALETTES: Record<Element, Palette[]> = {
  金: [
    { name: "SilverSteel", main: "#9aa0a8", sub: "#c8ccd2", highlight: "#ffffff", dark: "#5a5e66" },
    { name: "Gold",       main: "#d4a847", sub: "#f0c870", highlight: "#fff0b0", dark: "#7a5a18" },
    { name: "Copper",     main: "#c8784a", sub: "#e8a878", highlight: "#ffd8a8", dark: "#7a3a18" },
    { name: "Platinum",   main: "#e0e0e8", sub: "#f8f8ff", highlight: "#ffffff", dark: "#888a92" },
    { name: "Bronze",     main: "#a8784a", sub: "#c89a6a", highlight: "#e8c898", dark: "#5a3818" },
  ],
  木: [
    { name: "Emerald",    main: "#3a9a4a", sub: "#6ac87a", highlight: "#a8e8a8", dark: "#1a5a28" },
    { name: "Moss",       main: "#4a7a3a", sub: "#7aaa5a", highlight: "#b8d898", dark: "#2a4a18" },
    { name: "Bamboo",     main: "#8aaa4a", sub: "#b8d878", highlight: "#e0f0b0", dark: "#4a6a18" },
    { name: "Olive",      main: "#6a7a3a", sub: "#9aaa5a", highlight: "#d0e090", dark: "#3a4a18" },
    { name: "DeepWood",   main: "#5a4a2a", sub: "#8a7a5a", highlight: "#c0a878", dark: "#2a1a08" },
  ],
  水: [
    { name: "Abyss",      main: "#1a3a6a", sub: "#3a5a9a", highlight: "#88aaff", dark: "#0a1a3a" },
    { name: "Ice",        main: "#6ab8e8", sub: "#a8d8f8", highlight: "#e8f8ff", dark: "#2a5878" },
    { name: "DeepOcean",  main: "#0a2a4a", sub: "#1a4a6a", highlight: "#4a8aaa", dark: "#001020" },
    { name: "Cyan",       main: "#3ab8d8", sub: "#6ad8f0", highlight: "#a8f0ff", dark: "#1a5878" },
    { name: "Mist",       main: "#aac8d8", sub: "#c8dce8", highlight: "#e8f0f8", dark: "#5a7888" },
  ],
  火: [
    { name: "Crimson",    main: "#cc2a2a", sub: "#ee5a5a", highlight: "#ffb0b0", dark: "#7a0a0a" },
    { name: "Magma",      main: "#ff4a18", sub: "#ff8848", highlight: "#ffd878", dark: "#aa1a08" },
    { name: "Ember",      main: "#aa3a18", sub: "#ee7a48", highlight: "#ffb878", dark: "#5a1808" },
    { name: "Inferno",    main: "#ee1a1a", sub: "#ff5a3a", highlight: "#ffac6a", dark: "#8a0808" },
    { name: "Sunset",     main: "#e87a3a", sub: "#f0a868", highlight: "#ffd8a8", dark: "#8a3818" },
  ],
  土: [
    { name: "Sand",       main: "#c8a868", sub: "#e8c898", highlight: "#fff0c8", dark: "#7a5a28" },
    { name: "Earth",      main: "#7a5a38", sub: "#a88858", highlight: "#d8b888", dark: "#3a2818" },
    { name: "Clay",       main: "#a8784a", sub: "#c89868", highlight: "#e8c098", dark: "#5a3818" },
    { name: "Stone",      main: "#6a6a68", sub: "#9a9a98", highlight: "#c8c8c8", dark: "#2a2a28" },
    { name: "MossRock",   main: "#5a5a48", sub: "#888a68", highlight: "#b8c898", dark: "#2a2a18" },
  ],
};

/** 所有调色板（含元素名） */
export const ALL_PALETTES: { palette: Palette; element: Element }[] = [];
for (const el of ELEMENTS) {
  for (const p of ELEMENT_PALETTES[el]) {
    ALL_PALETTES.push({ palette: p, element: el });
  }
}

/** 根据名字选调色板（保证同怪同色） */
function hashStr(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

function pickPaletteForMonster(monster: MonsterDesc): Palette {
  const seed = hashStr(monster.scpId);
  const element = (monster.element ?? "土") as Element;
  const list = ELEMENT_PALETTES[element] ?? ELEMENT_PALETTES.土;
  return list[seed % list.length];
}

/** 画布辅助：直接写像素 */
function blankCanvas(): HTMLCanvasElement {
  const c = document.createElement("canvas");
  c.width = 64;
  c.height = 64;
  return c;
}

function fillRect(canvas: HTMLCanvasElement, x: number, y: number, w: number, h: number, color: string): void {
  const ctx = canvas.getContext("2d")!;
  if (w <= 0 || h <= 0) return;
  ctx.fillStyle = color;
  ctx.fillRect(Math.round(x), Math.round(y), Math.round(w), Math.round(h));
}

/** 简易种子 RNG */
class Rng {
  private state: number;
  constructor(seed: number) {
    this.state = seed || 0xdeadbeef;
  }
  next(): number {
    let x = this.state;
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    this.state = x >>> 0;
    return (this.state & 0x7fffffff) / 0x7fffffff;
  }
  pick<T>(arr: T[]): T {
    return arr[Math.floor(this.next() * arr.length)];
  }
  int(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
}

/** 程序化扰动（增加"活物"感） */
function perturb(canvas: HTMLCanvasElement, rng: Rng, amount: number, color: string): void {
  for (let i = 0; i < amount; i++) {
    const x = rng.int(2, 62);
    const y = rng.int(2, 62);
    const ctx = canvas.getContext("2d")!;
    const d = ctx.getImageData(x, y, 1, 1).data;
    if (d[3] > 0 && d[0] + d[1] + d[2] > 0) {
      fillRect(canvas, x, y, 1, 1, color);
    }
  }
}

// === 类型 ===
export type BodyType = string; // 25 个体型（5×5）
export interface PaletteEx extends Palette {
  bodyC: string;
  bodyLightC: string;
  bodyShadowC: string;
  sub: string;
  accent: string;
  text: string;
}

function expandPalette(p: Palette): PaletteEx {
  return {
    ...p,
    bodyC: p.main,
    bodyLightC: p.highlight,
    bodyShadowC: p.dark,
    sub: p.sub,
    accent: p.highlight,
    text: p.sub,
  };
}

// =================================================================
// ===                    25 个体型绘制函数                       ===
// =================================================================

// 工具：椭圆（矩形逼近）
function ellipse(c: HTMLCanvasElement, cx: number, cy: number, rx: number, ry: number, color: string): void {
  for (let y = -ry; y <= ry; y++) {
    for (let x = -rx; x <= rx; x++) {
      if ((x * x) / (rx * rx) + (y * y) / (ry * ry) <= 1) {
        fillRect(c, cx + x, cy + y, 1, 1, color);
      }
    }
  }
}

// === 金 ===

function drawBladeSoldier(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 锋刃兵：人形，双刃臂
  const cx = 32;
  const v2 = [0, 1, 2][v] ?? 0;
  const bodyTop = 22 + v2;
  // 身体（胸甲）
  fillRect(c, cx - 8, bodyTop, 16, 18, p.bodyC);
  fillRect(c, cx - 8, bodyTop, 16, 4, p.bodyLightC);
  fillRect(c, cx - 8, bodyTop + 14, 16, 4, p.bodyShadowC);
  // 头（头盔）
  fillRect(c, cx - 6, bodyTop - 8, 12, 10, p.bodyC);
  fillRect(c, cx - 6, bodyTop - 8, 12, 2, p.bodyLightC);
  fillRect(c, cx - 4, bodyTop - 6, 2, 2, p.sub);
  fillRect(c, cx + 2, bodyTop - 6, 2, 2, p.sub);
  // 头盔尖刺
  fillRect(c, cx, bodyTop - 12, 2, 4, p.bodyLightC);
  // 双刃手臂
  fillRect(c, cx - 16, bodyTop + 2, 6, 18, p.bodyC);
  fillRect(c, cx - 18, bodyTop + 18, 2, 8, p.bodyLightC);
  fillRect(c, cx + 10, bodyTop + 2, 6, 18, p.bodyC);
  fillRect(c, cx + 16, bodyTop + 18, 2, 8, p.bodyLightC);
  // 腿
  fillRect(c, cx - 6, bodyTop + 18, 4, 12, p.bodyShadowC);
  fillRect(c, cx + 2, bodyTop + 18, 4, 12, p.bodyShadowC);
  if (v2 === 1) {
    fillRect(c, cx - 2, bodyTop + 24, 4, 6, p.accent);
  }
}

function drawGearPuppet(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 齿轮傀：圆形齿轮身体
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32, cy = 32;
  const r = 18 + v2 * 2;
  // 齿轮外圈（带齿）
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const x = Math.round(cx + Math.cos(a) * r);
    const y = Math.round(cy + Math.sin(a) * r);
    fillRect(c, x - 1, y - 1, 3, 3, p.bodyC);
  }
  ellipse(c, cx, cy, r - 2, r - 2, p.bodyC);
  ellipse(c, cx, cy, r - 4, r - 4, p.bodyLightC);
  // 中心轴
  fillRect(c, cx - 2, cy - 2, 4, 4, p.bodyShadowC);
  fillRect(c, cx - 1, cy - 1, 2, 2, p.accent);
  // 指针（时针/分针）
  fillRect(c, cx, cy - r + 4, 1, r - 6, p.bodyShadowC);
  fillRect(c, cx + r - 6, cy, r - 8, 1, p.sub);
  // 铆钉
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2 + 0.3;
    const x = Math.round(cx + Math.cos(a) * (r - 8));
    const y = Math.round(cy + Math.sin(a) * (r - 8));
    fillRect(c, x, y, 2, 2, p.bodyShadowC);
  }
}

function drawSteelSkeleton(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 钢骨人：暴露骨架
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const y0 = 14 + v2;
  // 颅骨
  fillRect(c, cx - 6, y0, 12, 10, p.bodyC);
  fillRect(c, cx - 4, y0 + 2, 3, 4, p.bodyShadowC);
  fillRect(c, cx + 1, y0 + 2, 3, 4, p.bodyShadowC);
  // 脊柱
  fillRect(c, cx - 1, y0 + 10, 2, 22, p.bodyC);
  // 肋骨
  for (let i = 0; i < 4; i++) {
    fillRect(c, cx - 8, y0 + 12 + i * 4, 5, 1, p.bodyC);
    fillRect(c, cx + 3, y0 + 12 + i * 4, 5, 1, p.bodyC);
    fillRect(c, cx - 8, y0 + 12 + i * 4, 1, 4, p.bodyC);
    fillRect(c, cx + 7, y0 + 12 + i * 4, 1, 4, p.bodyC);
  }
  // 骨盆
  fillRect(c, cx - 6, y0 + 32, 12, 4, p.bodyC);
  // 腿骨
  fillRect(c, cx - 5, y0 + 36, 3, 12, p.bodyC);
  fillRect(c, cx + 2, y0 + 36, 3, 12, p.bodyC);
  // 手臂骨
  fillRect(c, cx - 12, y0 + 12, 4, 1, p.bodyC);
  fillRect(c, cx - 12, y0 + 13, 1, 12, p.bodyC);
  fillRect(c, cx + 8, y0 + 12, 4, 1, p.bodyC);
  fillRect(c, cx + 11, y0 + 13, 1, 12, p.bodyC);
  // 关节球
  fillRect(c, cx - 12, y0 + 12, 3, 3, p.bodyLightC);
  fillRect(c, cx + 9, y0 + 12, 3, 3, p.bodyLightC);
  fillRect(c, cx - 6, y0 + 24, 2, 2, p.bodyLightC);
}

function drawMirrorSpirit(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 镜灵：多边形反射体
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32, cy = 30;
  const sides = 6 + v2;
  // 多边形
  for (let y = -16; y <= 18; y++) {
    const t = (y + 16) / 34;
    const w = Math.sin(t * Math.PI) * 18;
    if (w < 1) continue;
    const shade = y < -8 ? p.bodyLightC : y < 4 ? p.bodyC : y < 12 ? p.sub : p.bodyShadowC;
    fillRect(c, cx - w, cy + y, w * 2, 1, shade);
  }
  // 内部多面
  for (let i = 0; i < sides; i++) {
    const a = (i / sides) * Math.PI * 2;
    const x = Math.round(cx + Math.cos(a) * 6);
    const y = Math.round(cy + Math.sin(a) * 6);
    fillRect(c, x, y, 2, 2, p.accent);
  }
  // 反光高光
  fillRect(c, cx - 12, cy - 8, 8, 2, "#ffffff");
  fillRect(c, cx - 14, cy - 6, 2, 4, "#ffffff");
}

function drawBronzeBell(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 铜钟：钟铃形
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 14 + v2;
  // 钟顶挂钩
  fillRect(c, cx, top - 6, 2, 4, p.bodyShadowC);
  fillRect(c, cx - 2, top - 4, 6, 2, p.bodyC);
  // 钟身
  for (let y = 0; y < 30; y++) {
    const w = 8 + Math.floor(y / 3);
    fillRect(c, cx - w, top + y, w * 2, 1, y < 10 ? p.bodyLightC : y < 22 ? p.bodyC : p.sub);
  }
  // 钟口
  fillRect(c, cx - 18, top + 28, 36, 3, p.bodyShadowC);
  // 铆钉
  for (let i = 0; i < 4; i++) {
    fillRect(c, cx - 14 + i * 10, top + 6, 2, 2, p.bodyShadowC);
  }
  // 钟摆
  fillRect(c, cx - 1, top + 30, 2, 6, p.bodyShadowC);
  fillRect(c, cx - 3, top + 36, 6, 4, p.accent);
}

// === 木 ===

function drawTreeElder(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 树人长老：树桩人形
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 12 + v2;
  // 树干身
  fillRect(c, cx - 10, top + 8, 20, 30, p.bodyC);
  // 年轮（同心圆）
  for (let r = 0; r < 8; r += 2) {
    ellipse(c, cx, top + 12, 6 + r, 4 + r / 2, r === 0 ? p.bodyLightC : p.bodyShadowC);
  }
  // 树冠
  fillRect(c, cx - 16, top - 4, 32, 14, p.bodyC);
  fillRect(c, cx - 16, top - 4, 32, 2, p.bodyLightC);
  fillRect(c, cx - 16, top + 8, 32, 2, p.bodyShadowC);
  // 树叶
  fillRect(c, cx - 14, top, 4, 4, p.sub);
  fillRect(c, cx + 10, top, 4, 4, p.sub);
  fillRect(c, cx - 4, top - 2, 4, 4, p.sub);
  // 根须
  for (let i = 0; i < 3; i++) {
    fillRect(c, cx - 12 + i * 8, top + 38, 4, 6, p.bodyShadowC);
    fillRect(c, cx - 14 + i * 8, top + 42, 2, 4, p.bodyShadowC);
    fillRect(c, cx + 4 + i * 4, top + 42, 2, 4, p.bodyShadowC);
  }
  // 眼
  fillRect(c, cx - 4, top + 16, 2, 2, p.accent);
  fillRect(c, cx + 2, top + 16, 2, 2, p.accent);
}

function drawVineHag(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 藤蔓女妖：缠绕
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32, cy = 32;
  // 中心身体
  ellipse(c, cx, cy, 10, 12, p.bodyC);
  fillRect(c, cx - 8, cy - 4, 16, 2, p.bodyLightC);
  // 眼
  fillRect(c, cx - 4, cy - 2, 2, 2, p.accent);
  fillRect(c, cx + 2, cy - 2, 2, 2, p.accent);
  // 嘴
  fillRect(c, cx - 2, cy + 4, 4, 1, p.bodyShadowC);
  // 触手
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2 + v2 * 0.4;
    const len = 12 + (i % 3) * 4;
    for (let j = 1; j <= len; j++) {
      const wob = Math.sin(j * 0.3 + i) * 2;
      const x = Math.round(cx + Math.cos(a) * j + wob);
      const y = Math.round(cy + Math.sin(a) * j);
      const col = j < 4 ? p.bodyC : p.bodyShadowC;
      fillRect(c, x, y, 1, 1, col);
    }
  }
  // 叶子
  for (let i = 0; i < 4; i++) {
    const a = (i / 4) * Math.PI * 2;
    const x = Math.round(cx + Math.cos(a) * 18);
    const y = Math.round(cy + Math.sin(a) * 18);
    fillRect(c, x, y, 3, 3, p.sub);
  }
}

function drawMushroomSpirit(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 菌伞灵：蘑菇
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const baseY = 24 + v2;
  // 伞盖
  fillRect(c, cx - 22, baseY, 44, 6, p.bodyC);
  for (let x = -22; x <= 22; x += 2) {
    const h = Math.max(0, 12 - Math.abs(x) / 2);
    fillRect(c, cx + x, baseY - h, 2, h, p.bodyC);
  }
  fillRect(c, cx - 22, baseY, 44, 2, p.bodyLightC);
  // 斑点
  for (let i = 0; i < 5; i++) {
    fillRect(c, cx - 14 + i * 7, baseY - 4, 3, 3, p.accent);
  }
  // 柄
  fillRect(c, cx - 4, baseY + 6, 8, 22, p.sub);
  fillRect(c, cx - 4, baseY + 6, 2, 22, p.bodyLightC);
  fillRect(c, cx + 2, baseY + 6, 2, 22, p.bodyShadowC);
  // 眼
  fillRect(c, cx - 3, baseY + 12, 2, 2, p.accent);
  fillRect(c, cx + 1, baseY + 12, 2, 2, p.accent);
  // 嘴
  fillRect(c, cx - 2, baseY + 18, 4, 1, p.bodyShadowC);
  // 根须
  for (let i = 0; i < 3; i++) {
    fillRect(c, cx - 6 + i * 4, baseY + 28, 2, 4, p.bodyShadowC);
  }
}

function drawBambooSprite(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 竹节妖：竹节分段
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 12 + v2;
  // 顶节
  fillRect(c, cx - 6, top, 12, 6, p.bodyC);
  fillRect(c, cx - 6, top, 12, 1, p.bodyLightC);
  // 节疤
  fillRect(c, cx - 7, top + 5, 14, 1, p.bodyShadowC);
  fillRect(c, cx - 8, top + 5, 16, 2, p.bodyLightC);
  fillRect(c, cx - 7, top + 6, 14, 1, p.bodyShadowC);
  // 竹身节
  fillRect(c, cx - 7, top + 8, 14, 10, p.bodyC);
  fillRect(c, cx - 7, top + 8, 2, 10, p.bodyLightC);
  fillRect(c, cx + 5, top + 8, 2, 10, p.bodyShadowC);
  fillRect(c, cx - 8, top + 17, 16, 2, p.bodyLightC);
  fillRect(c, cx - 7, top + 19, 14, 10, p.bodyC);
  fillRect(c, cx - 8, top + 28, 16, 2, p.bodyLightC);
  fillRect(c, cx - 7, top + 30, 14, 10, p.bodyC);
  // 笋尖
  fillRect(c, cx, top - 6, 2, 6, p.bodyC);
  fillRect(c, cx, top - 6, 1, 6, p.bodyLightC);
  // 眼
  fillRect(c, cx - 3, top + 12, 2, 2, p.accent);
  fillRect(c, cx + 1, top + 12, 2, 2, p.accent);
}

function drawWoodenKite(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 木鸢：木雕鸟
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32, cy = 28 + v2;
  // 身体（鸟身）
  ellipse(c, cx, cy, 10, 8, p.bodyC);
  fillRect(c, cx - 10, cy - 4, 20, 2, p.bodyLightC);
  // 翅
  fillRect(c, cx - 22, cy - 6, 14, 8, p.bodyC);
  fillRect(c, cx - 22, cy - 6, 14, 2, p.bodyLightC);
  fillRect(c, cx - 22, cy, 14, 2, p.bodyShadowC);
  fillRect(c, cx + 8, cy - 6, 14, 8, p.bodyC);
  fillRect(c, cx + 8, cy - 6, 14, 2, p.bodyLightC);
  fillRect(c, cx + 8, cy, 14, 2, p.bodyShadowC);
  // 眼
  fillRect(c, cx - 4, cy - 2, 3, 3, p.sub);
  fillRect(c, cx - 3, cy - 1, 1, 1, p.accent);
  // 喙
  fillRect(c, cx + 8, cy - 1, 6, 2, p.bodyShadowC);
  fillRect(c, cx + 14, cy - 2, 2, 4, p.accent);
  // 尾
  fillRect(c, cx - 2, cy + 6, 4, 8, p.bodyC);
  fillRect(c, cx - 2, cy + 14, 4, 2, p.bodyShadowC);
  // 脚
  fillRect(c, cx - 4, cy + 6, 1, 4, p.bodyShadowC);
  fillRect(c, cx + 3, cy + 6, 1, 4, p.bodyShadowC);
}

// === 水 ===

function drawJellyfishLantern(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 水母灯：漂浮水母
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 10 + v2;
  // 伞冠
  for (let y = 0; y < 16; y++) {
    const w = Math.floor(Math.sin(y / 16 * Math.PI) * 18);
    const col = y < 4 ? p.bodyLightC : y < 10 ? p.bodyC : p.sub;
    fillRect(c, cx - w, top + y, w * 2, 1, col);
  }
  // 发光核心
  fillRect(c, cx - 4, top + 8, 8, 8, p.accent);
  fillRect(c, cx - 2, top + 10, 4, 4, "#ffffff");
  // 触手
  for (let i = 0; i < 6; i++) {
    const x = cx - 12 + i * 5;
    for (let j = 0; j < 18; j++) {
      const wob = Math.sin(j * 0.3 + i) * 2;
      const col = j < 8 ? p.bodyC : p.sub;
      fillRect(c, x + wob, top + 16 + j, 1, 1, col);
    }
  }
}

function drawIceGhost(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 冰棱鬼：冰晶人形
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 12 + v2;
  // 头（八面体冰晶）
  for (let y = 0; y < 14; y++) {
    const w = Math.floor(Math.sin(y / 14 * Math.PI) * 10);
    const col = y < 4 ? p.bodyLightC : y < 10 ? p.bodyC : p.sub;
    fillRect(c, cx - w, top + y, w * 2, 1, col);
  }
  // 身体（棱柱）
  fillRect(c, cx - 10, top + 14, 20, 4, p.bodyC);
  fillRect(c, cx - 8, top + 18, 16, 12, p.bodyC);
  fillRect(c, cx - 8, top + 18, 2, 12, p.bodyLightC);
  fillRect(c, cx + 6, top + 18, 2, 12, p.bodyShadowC);
  // 腿（冰柱）
  fillRect(c, cx - 6, top + 30, 4, 10, p.bodyC);
  fillRect(c, cx + 2, top + 30, 4, 10, p.bodyC);
  // 手臂（冰棱）
  fillRect(c, cx - 16, top + 16, 6, 2, p.bodyC);
  fillRect(c, cx - 18, top + 14, 2, 4, p.bodyLightC);
  fillRect(c, cx + 10, top + 16, 6, 2, p.bodyC);
  fillRect(c, cx + 16, top + 14, 2, 4, p.bodyLightC);
  // 眼
  fillRect(c, cx - 3, top + 22, 2, 2, p.accent);
  fillRect(c, cx + 1, top + 22, 2, 2, p.accent);
}

function drawTideSpirit(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 潮汐灵：浪花拟人
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const baseY = 14 + v2;
  // 浪头（人头）
  for (let y = 0; y < 18; y++) {
    const w = Math.floor(Math.sin(y / 18 * Math.PI) * 12) + (y === 4 ? 6 : y === 6 ? 4 : 0);
    const col = y < 6 ? p.bodyLightC : y < 14 ? p.bodyC : p.sub;
    fillRect(c, cx - w, baseY + y, w * 2, 1, col);
  }
  // 浪身
  for (let y = 0; y < 24; y++) {
    const w = Math.floor(8 + Math.sin(y * 0.5) * 2);
    const col = y < 8 ? p.bodyC : y < 18 ? p.sub : p.bodyShadowC;
    fillRect(c, cx - w, baseY + 18 + y, w * 2, 1, col);
  }
  // 水珠装饰
  for (let i = 0; i < 6; i++) {
    fillRect(c, cx - 12 + i * 4, baseY + i * 2, 2, 2, p.accent);
  }
  // 眼
  fillRect(c, cx - 4, baseY + 6, 2, 2, p.accent);
  fillRect(c, cx + 2, baseY + 6, 2, 2, p.accent);
}

function drawRainDropSprite(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 雨滴精：雨滴身形
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 10 + v2;
  // 滴头（尖头水滴）
  for (let y = 0; y < 12; y++) {
    const w = Math.floor(Math.sin(y / 12 * Math.PI) * 10);
    const col = y < 4 ? p.bodyLightC : y < 8 ? p.bodyC : p.sub;
    fillRect(c, cx - w, top + y, w * 2, 1, col);
  }
  fillRect(c, cx, top - 4, 1, 4, p.bodyC);
  // 滴身
  for (let y = 0; y < 28; y++) {
    const w = Math.floor(10 - Math.abs(y - 14) / 4);
    const col = y < 10 ? p.bodyC : y < 22 ? p.sub : p.bodyShadowC;
    fillRect(c, cx - w, top + 12 + y, w * 2, 1, col);
  }
  // 高光
  fillRect(c, cx - 8, top + 14, 2, 12, p.bodyLightC);
  // 眼
  fillRect(c, cx - 4, top + 20, 2, 2, p.accent);
  fillRect(c, cx + 2, top + 20, 2, 2, p.accent);
  // 雨线（尾）
  fillRect(c, cx - 1, top + 44, 1, 6, p.sub);
  fillRect(c, cx + 1, top + 44, 1, 4, p.sub);
}

function drawDeepDiver(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 深潜者：章鱼深海怪
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32, cy = 26 + v2;
  // 头部（圆顶）
  for (let y = -10; y <= 8; y++) {
    const w = Math.floor(Math.sqrt(Math.max(0, 100 - y * y)));
    const col = y < -4 ? p.bodyLightC : y < 4 ? p.bodyC : p.sub;
    fillRect(c, cx - w, cy + y, w * 2, 1, col);
  }
  // 眼（竖瞳）
  fillRect(c, cx - 6, cy - 4, 2, 6, p.sub);
  fillRect(c, cx - 5, cy - 2, 1, 2, p.accent);
  fillRect(c, cx + 4, cy - 4, 2, 6, p.sub);
  fillRect(c, cx + 4, cy - 2, 1, 2, p.accent);
  // 多触手
  for (let i = 0; i < 8; i++) {
    const a = Math.PI / 2 + (i / 8) * Math.PI;
    const len = 16 + (i % 3) * 4;
    for (let j = 1; j <= len; j++) {
      const wob = Math.sin(j * 0.3 + i) * 2;
      const x = Math.round(cx + Math.cos(a) * j + wob);
      const y = Math.round(cy + 8 + Math.sin(a) * j);
      const col = j < 6 ? p.bodyC : p.bodyShadowC;
      fillRect(c, x, y, 1, 1, col);
    }
  }
  // 灯笼诱饵（头顶）
  fillRect(c, cx, cy - 14, 2, 2, p.accent);
}

// === 火 ===

function drawCandleSprite(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 烛灵：蜡烛人形
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 14 + v2;
  // 烛芯
  fillRect(c, cx, top - 6, 1, 4, p.bodyShadowC);
  // 火焰
  for (let y = -12; y < -6; y++) {
    const w = Math.max(1, Math.floor(Math.sin((y + 12) / 6 * Math.PI) * 3));
    const col = y < -10 ? p.accent : y < -8 ? p.sub : p.bodyLightC;
    fillRect(c, cx - w, top + y, w * 2, 1, col);
  }
  // 蜡身
  fillRect(c, cx - 6, top - 2, 12, 30, p.bodyC);
  fillRect(c, cx - 6, top - 2, 2, 30, p.bodyLightC);
  fillRect(c, cx + 4, top - 2, 2, 30, p.bodyShadowC);
  // 蜡泪
  for (let i = 0; i < 3; i++) {
    fillRect(c, cx - 4 + i * 4, top + 4 + i * 6, 1, 2 + i, p.bodyLightC);
  }
  // 脸（蜡上刻）
  fillRect(c, cx - 3, top + 8, 2, 2, p.bodyShadowC);
  fillRect(c, cx + 1, top + 8, 2, 2, p.bodyShadowC);
  fillRect(c, cx - 2, top + 14, 4, 1, p.bodyShadowC);
  // 烛台底
  fillRect(c, cx - 8, top + 28, 16, 4, p.bodyShadowC);
}

function drawFlameDemon(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 炎魔：火焰巨灵
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 8 + v2;
  // 火焰头
  for (let y = 0; y < 18; y++) {
    const w = Math.floor(Math.sin(y / 18 * Math.PI) * 12) + (y === 6 ? 4 : 0);
    const col = y < 4 ? p.bodyLightC : y < 10 ? p.bodyC : y < 14 ? p.sub : p.bodyShadowC;
    fillRect(c, cx - w, top + y, w * 2, 1, col);
  }
  // 火焰尖
  fillRect(c, cx - 2, top - 4, 4, 4, p.bodyC);
  fillRect(c, cx, top - 6, 2, 2, p.accent);
  // 火焰身体
  fillRect(c, cx - 12, top + 18, 24, 24, p.bodyC);
  fillRect(c, cx - 12, top + 18, 24, 4, p.bodyLightC);
  fillRect(c, cx - 12, top + 38, 24, 4, p.bodyShadowC);
  // 岩浆血管
  for (let i = 0; i < 4; i++) {
    fillRect(c, cx - 10 + i * 6, top + 24 + (i % 2) * 8, 2, 6, p.accent);
  }
  // 眼（灼热）
  fillRect(c, cx - 4, top + 24, 3, 3, p.accent);
  fillRect(c, cx + 1, top + 24, 3, 3, p.accent);
  // 嘴
  fillRect(c, cx - 4, top + 32, 8, 1, p.bodyShadowC);
}

function drawEmberHerald(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 灰烬使：灰烬人形
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 14 + v2;
  // 灰身
  fillRect(c, cx - 10, top + 6, 20, 30, p.bodyShadowC);
  // 灰烬高光
  for (let i = 0; i < 6; i++) {
    fillRect(c, cx - 8 + (i % 3) * 6, top + 8 + Math.floor(i / 3) * 8, 4, 4, p.bodyC);
  }
  // 头
  fillRect(c, cx - 7, top - 4, 14, 12, p.bodyShadowC);
  fillRect(c, cx - 7, top - 4, 14, 2, p.bodyC);
  // 余烬眼
  fillRect(c, cx - 4, top, 2, 2, p.accent);
  fillRect(c, cx + 2, top, 2, 2, p.accent);
  // 嘴（灰缝）
  fillRect(c, cx - 2, top + 6, 4, 1, p.accent);
  // 飘散灰屑
  for (let i = 0; i < 8; i++) {
    const x = cx - 16 + i * 4;
    const y = top - 8 - (i % 2) * 4;
    fillRect(c, x, y, 2, 2, p.bodyC);
    fillRect(c, x + 1, y - 2, 1, 1, p.accent);
  }
  // 腿（短）
  fillRect(c, cx - 6, top + 36, 4, 8, p.bodyShadowC);
  fillRect(c, cx + 2, top + 36, 4, 8, p.bodyShadowC);
}

function drawLavaBeast(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 岩浆兽：熔岩怪兽
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32, cy = 32;
  // 身体
  for (let y = -12; y <= 14; y++) {
    const w = Math.floor(Math.sqrt(Math.max(0, 144 - y * y))) + v2;
    const col = y < -8 ? p.bodyLightC : y < 4 ? p.bodyC : y < 10 ? p.sub : p.bodyShadowC;
    fillRect(c, cx - w, cy + y, w * 2, 1, col);
  }
  // 裂缝（亮）
  for (let i = 0; i < 5; i++) {
    const sx = cx - 12 + i * 6;
    const sy = cy - 4 + (i % 2) * 6;
    fillRect(c, sx, sy, 4, 1, p.accent);
    fillRect(c, sx + 1, sy + 1, 2, 1, p.accent);
  }
  // 眼
  fillRect(c, cx - 6, cy - 4, 3, 3, p.accent);
  fillRect(c, cx + 3, cy - 4, 3, 3, p.accent);
  // 嘴
  fillRect(c, cx - 4, cy + 6, 8, 1, p.accent);
  // 滴落岩浆
  fillRect(c, cx + 8, cy + 8, 2, 2, p.accent);
  fillRect(c, cx - 12, cy + 4, 2, 2, p.accent);
}

function drawSulfurGhost(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 硫磺鬼：硫磺烟雾
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32, cy = 30;
  // 烟雾（多层叠加）
  for (let layer = 0; layer < 3; layer++) {
    const r = 12 + layer * 4 + v2;
    for (let y = -r; y <= r; y++) {
      for (let x = -r; x <= r; x++) {
        const dist = Math.sqrt(x * x + y * y);
        if (dist > r - 3 && dist < r) {
          const col = layer === 0 ? p.bodyLightC : layer === 1 ? p.bodyC : p.sub;
          fillRect(c, cx + x, cy + y, 1, 1, col);
        }
      }
    }
  }
  // 硫磺结晶（黄色亮点）
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * Math.PI * 2;
    const x = Math.round(cx + Math.cos(a) * 12);
    const y = Math.round(cy + Math.sin(a) * 12);
    fillRect(c, x, y, 2, 2, p.accent);
  }
  // 内部红眼
  fillRect(c, cx - 4, cy - 2, 2, 4, p.accent);
  fillRect(c, cx + 2, cy - 2, 2, 4, p.accent);
}

// === 土 ===

function drawMountainGiant(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 山岭巨灵：石人巨像
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 8 + v2;
  // 头部（不规则岩石）
  for (let y = 0; y < 14; y++) {
    const w = Math.floor(8 + Math.sin(y * 0.7) * 2);
    const col = y < 4 ? p.bodyLightC : y < 10 ? p.bodyC : p.sub;
    fillRect(c, cx - w, top + y, w * 2, 1, col);
  }
  // 苔藓斑
  fillRect(c, cx - 6, top + 4, 3, 3, p.accent);
  fillRect(c, cx + 4, top + 6, 2, 2, p.accent);
  // 眼
  fillRect(c, cx - 4, top + 8, 2, 2, p.bodyShadowC);
  fillRect(c, cx + 2, top + 8, 2, 2, p.bodyShadowC);
  // 身体（巨岩）
  fillRect(c, cx - 14, top + 14, 28, 28, p.bodyC);
  fillRect(c, cx - 14, top + 14, 28, 4, p.bodyLightC);
  fillRect(c, cx - 14, top + 38, 28, 4, p.bodyShadowC);
  // 岩层横线
  for (let i = 0; i < 4; i++) {
    fillRect(c, cx - 14, top + 20 + i * 6, 28, 1, p.bodyShadowC);
  }
  // 手臂
  fillRect(c, cx - 22, top + 18, 8, 18, p.bodyC);
  fillRect(c, cx - 22, top + 18, 2, 18, p.bodyLightC);
  fillRect(c, cx + 14, top + 18, 8, 18, p.bodyC);
  fillRect(c, cx + 14, top + 18, 2, 18, p.bodyLightC);
  // 腿
  fillRect(c, cx - 10, top + 42, 6, 8, p.bodyShadowC);
  fillRect(c, cx + 4, top + 42, 6, 8, p.bodyShadowC);
}

function drawClayChild(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 泥塑童：泥人玩偶
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 14 + v2;
  // 头
  ellipse(c, cx, top + 6, 8, 8, p.bodyC);
  fillRect(c, cx - 8, top + 2, 16, 2, p.bodyLightC);
  // 裂纹
  fillRect(c, cx - 4, top + 6, 1, 4, p.bodyShadowC);
  fillRect(c, cx + 4, top + 4, 1, 6, p.bodyShadowC);
  // 眼
  fillRect(c, cx - 4, top + 6, 2, 2, p.bodyShadowC);
  fillRect(c, cx + 2, top + 6, 2, 2, p.bodyShadowC);
  // 嘴
  fillRect(c, cx - 2, top + 10, 4, 1, p.bodyShadowC);
  // 身
  fillRect(c, cx - 7, top + 14, 14, 18, p.bodyC);
  fillRect(c, cx - 7, top + 14, 14, 2, p.bodyLightC);
  // 泥手印（装饰）
  for (let i = 0; i < 3; i++) {
    fillRect(c, cx - 6 + i * 4, top + 18 + i * 2, 2, 2, p.sub);
  }
  // 臂
  fillRect(c, cx - 12, top + 16, 4, 14, p.bodyC);
  fillRect(c, cx + 8, top + 16, 4, 14, p.bodyC);
  // 腿
  fillRect(c, cx - 5, top + 32, 4, 12, p.bodyShadowC);
  fillRect(c, cx + 1, top + 32, 4, 12, p.bodyShadowC);
}

function drawSandEffigy(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 沙偶：沙粒人形
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 12 + v2;
  // 金字塔头（三角形）
  for (let y = 0; y < 14; y++) {
    const w = Math.floor((14 - y) / 2) + 1;
    const col = y < 4 ? p.bodyLightC : y < 10 ? p.bodyC : p.sub;
    fillRect(c, cx - w, top + y, w * 2, 1, col);
  }
  // 沙流（顶冠）
  fillRect(c, cx, top - 4, 1, 4, p.sub);
  // 眼
  fillRect(c, cx - 3, top + 8, 2, 2, p.bodyShadowC);
  fillRect(c, cx + 1, top + 8, 2, 2, p.bodyShadowC);
  // 身（沙粒）
  fillRect(c, cx - 8, top + 14, 16, 22, p.bodyC);
  fillRect(c, cx - 8, top + 14, 16, 2, p.bodyLightC);
  // 沙粒装饰
  for (let i = 0; i < 10; i++) {
    fillRect(c, cx - 6 + (i * 13) % 12, top + 18 + Math.floor(i / 3) * 4, 1, 1, p.sub);
  }
  // 臂
  fillRect(c, cx - 14, top + 16, 4, 14, p.bodyC);
  fillRect(c, cx + 10, top + 16, 4, 14, p.bodyC);
  // 腿
  fillRect(c, cx - 6, top + 36, 4, 10, p.bodyShadowC);
  fillRect(c, cx + 2, top + 36, 4, 10, p.bodyShadowC);
  // 底部沙堆
  fillRect(c, cx - 12, top + 46, 24, 4, p.bodyShadowC);
}

function drawOreBeast(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 矿石兽：矿石怪兽
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32, cy = 32;
  const r = 14 + v2;
  // 矿石主体（不规则）
  for (let y = -r; y <= r; y++) {
    for (let x = -r; x <= r; x++) {
      const dist = Math.sqrt(x * x + y * y);
      const wobble = Math.sin(x * 0.7 + y * 0.3) * 1.5;
      if (dist < r + wobble && dist > r - 4) {
        const col = y < -r / 2 ? p.bodyLightC : y < r / 2 ? p.bodyC : p.sub;
        fillRect(c, cx + x, cy + y, 1, 1, col);
      }
    }
  }
  // 晶簇（背面）
  for (let i = 0; i < 5; i++) {
    const x = cx - 6 + i * 3;
    const y = cy - r - 4;
    fillRect(c, x, y, 2, 4, p.bodyC);
    fillRect(c, x, y, 1, 4, p.bodyLightC);
  }
  // 矿脉纹
  for (let i = 0; i < 4; i++) {
    fillRect(c, cx - 8 + i * 4, cy + 4 + (i % 2) * 6, 4, 1, p.accent);
  }
  // 眼
  fillRect(c, cx - 5, cy - 4, 2, 3, p.bodyShadowC);
  fillRect(c, cx + 3, cy - 4, 2, 3, p.bodyShadowC);
  // 嘴
  fillRect(c, cx - 3, cy + 4, 6, 1, p.bodyShadowC);
  // 四足
  for (let i = 0; i < 4; i++) {
    const px = cx + (i % 2 === 0 ? -10 : 8);
    const py = cy + (i < 2 ? 10 : 14);
    fillRect(c, px, py, 3, 4, p.bodyShadowC);
  }
}

function drawTerracotta(c: HTMLCanvasElement, v: number, p: PaletteEx): void {
  // 陶俑：古陶俑兵
  const v2 = [0, 1, 2][v] ?? 0;
  const cx = 32;
  const top = 8 + v2;
  // 俑冠
  fillRect(c, cx - 8, top, 16, 4, p.bodyShadowC);
  fillRect(c, cx - 10, top + 2, 20, 2, p.bodyC);
  // 头
  fillRect(c, cx - 7, top + 4, 14, 12, p.bodyC);
  fillRect(c, cx - 7, top + 4, 14, 2, p.bodyLightC);
  // 眼
  fillRect(c, cx - 4, top + 8, 2, 2, p.bodyShadowC);
  fillRect(c, cx + 2, top + 8, 2, 2, p.bodyShadowC);
  // 嘴
  fillRect(c, cx - 2, top + 12, 4, 1, p.bodyShadowC);
  // 身
  fillRect(c, cx - 8, top + 16, 16, 24, p.bodyC);
  fillRect(c, cx - 8, top + 16, 16, 2, p.bodyLightC);
  // 陶裂纹
  fillRect(c, cx - 4, top + 18, 1, 8, p.bodyShadowC);
  fillRect(c, cx + 2, top + 22, 1, 12, p.bodyShadowC);
  fillRect(c, cx - 6, top + 30, 1, 6, p.bodyShadowC);
  // 铠甲线
  fillRect(c, cx - 8, top + 24, 16, 1, p.bodyShadowC);
  fillRect(c, cx - 8, top + 32, 16, 1, p.bodyShadowC);
  // 臂
  fillRect(c, cx - 14, top + 18, 4, 18, p.bodyC);
  fillRect(c, cx + 10, top + 18, 4, 18, p.bodyC);
  // 拳
  fillRect(c, cx - 14, top + 34, 4, 4, p.bodyShadowC);
  fillRect(c, cx + 10, top + 34, 4, 4, p.bodyShadowC);
  // 腿
  fillRect(c, cx - 6, top + 40, 4, 8, p.bodyShadowC);
  fillRect(c, cx + 2, top + 40, 4, 8, p.bodyShadowC);
}

// === 25 体型分发器 ===
const DRAWERS: Record<string, (c: HTMLCanvasElement, v: number, p: PaletteEx) => void> = {
  // 金
  "锋刃兵": drawBladeSoldier,
  "齿轮傀": drawGearPuppet,
  "钢骨人": drawSteelSkeleton,
  "镜灵":   drawMirrorSpirit,
  "铜钟":   drawBronzeBell,
  // 木
  "树人长老": drawTreeElder,
  "藤蔓女妖": drawVineHag,
  "菌伞灵":  drawMushroomSpirit,
  "竹节妖":  drawBambooSprite,
  "木鸢":    drawWoodenKite,
  // 水
  "水母灯":  drawJellyfishLantern,
  "冰棱鬼":  drawIceGhost,
  "潮汐灵":  drawTideSpirit,
  "雨滴精":  drawRainDropSprite,
  "深潜者":  drawDeepDiver,
  // 火
  "烛灵":    drawCandleSprite,
  "炎魔":    drawFlameDemon,
  "灰烬使":  drawEmberHerald,
  "岩浆兽":  drawLavaBeast,
  "硫磺鬼":  drawSulfurGhost,
  // 土
  "山岭巨灵": drawMountainGiant,
  "泥塑童":   drawClayChild,
  "沙偶":     drawSandEffigy,
  "矿石兽":   drawOreBeast,
  "陶俑":     drawTerracotta,
};

// === 25 体型变体索引（每体型随机 0/1/2） ===
const VARIANT_COUNT = 3;

function pickVariant(scpId: string): 0 | 1 | 2 {
  return (hashStr(scpId) % VARIANT_COUNT) as 0 | 1 | 2;
}

function pickBodyType(monster: MonsterDesc): string {
  if (monster.bodyType && DRAWERS[monster.bodyType]) return monster.bodyType;
  // fallback: 基于元素 + scpId 哈希
  const element = (monster.element ?? "土") as Element;
  const seed = hashStr(monster.scpId);
  const list = ALL_BODY_TYPES;
  let offset = 0;
  for (const el of ELEMENTS) {
    const elList = ELEMENTS;
    if (el === element) break;
    offset += 5;
  }
  return list[(offset + (seed % 5)) % list.length];
}

// === 程序化怪物生成 ===
export interface MonsterFrames {
  frame1: ImageData;
  frame2: ImageData;
}

export function generateMonster(
  monster: MonsterDesc,
  _features?: Set<string>,
  bodyTypeHint?: string,
  paletteHint?: string,
  variantHint?: number
): MonsterFrames {
  const p = expandPalette(
    paletteHint && ELEMENTS.some(() => true)
      ? ALL_PALETTES.find((ap) => ap.palette.name === paletteHint)?.palette ?? pickPaletteForMonster(monster)
      : pickPaletteForMonster(monster)
  );
  const bodyType = bodyTypeHint && DRAWERS[bodyTypeHint] ? bodyTypeHint : pickBodyType(monster);
  const variant = (variantHint !== undefined ? variantHint : pickVariant(monster.scpId)) as 0 | 1 | 2;

  // 帧 1
  const c1 = blankCanvas();
  DRAWERS[bodyType](c1, variant, p);
  // 帧 2：轻微扰动（呼吸感）
  const c2 = blankCanvas();
  DRAWERS[bodyType](c2, variant === 2 ? 0 : (variant + 1) as 0 | 1 | 2, p);

  // 程序化扰动（增加活物感）
  const seed = hashStr(monster.scpId);
  const rng = new Rng(seed);
  if (rng.next() < 0.5) {
    perturb(c1, rng, rng.int(1, 4), p.bodyShadowC);
    perturb(c2, rng, rng.int(1, 4), p.bodyShadowC);
  }

  const ctx1 = c1.getContext("2d")!;
  const ctx2 = c2.getContext("2d")!;
  return {
    frame1: ctx1.getImageData(0, 0, 64, 64),
    frame2: ctx2.getImageData(0, 0, 64, 64),
  };
}

/** 2 帧循环动画 */
export function animateIdle(
  canvas: HTMLCanvasElement,
  frames: MonsterFrames,
  fps: number = 5
): () => void {
  const ctx = canvas.getContext("2d")!;
  ctx.imageSmoothingEnabled = false;
  let frame = 0;
  let running = true;
  const tmp = document.createElement("canvas");
  tmp.width = 64;
  tmp.height = 64;
  function tick() {
    if (!running) return;
    const data = frame % 2 === 0 ? frames.frame1 : frames.frame2;
    tmp.getContext("2d")!.putImageData(data, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(tmp, 0, 0, canvas.width, canvas.height);
    frame++;
    setTimeout(tick, 1000 / fps);
  }
  tick();
  return () => { running = false; };
}

/** 兼容旧 API（screen4 等用） */
export const BODY_TYPES = ALL_BODY_TYPES;